// apps/web/src/pages/home/HomePage.tsx
//
// Landing page at "/" — replaces the placeholder `<div>The Recipe Spread</div>`
// stub so a reviewer has a real entry point instead of having to hand-build view
// URLs from UUIDs.
//
// - Signed out: prompt sign-in (the list endpoints + view routes are gated to
//   can_author; guest/public browsing is a later phase).
// - Signed in: list the seeded spreads + recipes with links to the authed view
//   routes (/spreads/:id/view, /recipes/:id/view). The :id is the UUID returned
//   by the list endpoints; RLS already scopes the results to what the actor may read.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../lib/api-client";
import { useSession } from "../../lib/auth-client";

interface ListItem {
  id: string;
  owner_id: string;
  visibility: "private" | "public" | "shared";
  slug: string;
}

interface ListResponse {
  items: ListItem[];
  count: number;
}

export function HomePage() {
  const { data: session, isPending } = useSession();
  const signedIn = !isPending && session?.user != null;

  const spreadsQuery = useQuery<ListResponse>({
    queryKey: ["spreads", "list"],
    enabled: signedIn,
    queryFn: () => api<ListResponse>("/api/spreads"),
  });
  const recipesQuery = useQuery<ListResponse>({
    queryKey: ["recipes", "list"],
    enabled: signedIn,
    queryFn: () => api<ListResponse>("/api/recipes"),
  });

  return (
    <main aria-label="The Recipe Spread — home" className="mx-auto min-h-[60vh] max-w-3xl px-6 py-10">
      <h1 className="text-3xl font-semibold tracking-tight">The Recipe Spread</h1>

      {!signedIn ? (
        <section className="mt-6">
          <p className="text-gray-600">Sign in to view the recipes and spreads.</p>
          <Link
            to="/auth/sign-in"
            className="mt-4 inline-block rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            Sign in
          </Link>
        </section>
      ) : (
        <div className="mt-8 space-y-10">
          <ListSection
            title="Spreads"
            emptyLabel="No spreads found."
            isLoading={spreadsQuery.isLoading}
            isError={spreadsQuery.isError}
            items={spreadsQuery.data?.items}
            hrefFor={(item) => `/spreads/${item.id}/view`}
          />
          <ListSection
            title="Recipes"
            emptyLabel="No recipes found."
            isLoading={recipesQuery.isLoading}
            isError={recipesQuery.isError}
            items={recipesQuery.data?.items}
            hrefFor={(item) => `/recipes/${item.id}/view`}
          />
        </div>
      )}
    </main>
  );
}

interface ListSectionProps {
  title: string;
  emptyLabel: string;
  isLoading: boolean;
  isError: boolean;
  items: ListItem[] | undefined;
  hrefFor: (item: ListItem) => string;
}

function ListSection({ title, emptyLabel, isLoading, isError, items, hrefFor }: ListSectionProps) {
  return (
    <section aria-label={title}>
      <h2 className="text-xl font-medium">{title}</h2>
      {isLoading ? (
        <p aria-busy="true" className="mt-2 text-gray-500">
          Loading…
        </p>
      ) : isError ? (
        <p className="mt-2 text-red-600">Could not load {title.toLowerCase()}.</p>
      ) : items === undefined || items.length === 0 ? (
        <p className="mt-2 text-gray-500">{emptyLabel}</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200">
          {items.map((item) => (
            <li key={item.id}>
              <Link
                to={hrefFor(item)}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
              >
                <span className="font-medium">{item.slug}</span>
                <span className="text-sm text-gray-500">{item.visibility}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default HomePage;
